import * as React from 'react'
import styled from 'styled-components'
import { darken } from 'polished'
import { css } from 'styled-components'
import { config, Transition } from 'react-spring'
import { media } from '@webnuts/shared-components'

const stepResolver = (state: StepState, complete: any, active: any, inactive: any) => {
  return state === StepState.completed ? complete : state === StepState.active ? active : inactive
}

const Container = styled.div<{ state: StepState }>`
  margin-bottom: 16px;
  box-shadow: 0 0 ${(props) => (props.state === StepState.active ? 15 : 8)}px 0
    ${(props) => darken(props.state === StepState.active ? 0.05 : 0, props.theme.color.greyLight)};
  border-radius: 3px;
`

const Header = styled.div<{ state: StepState }>`
  pointer-events: ${(props) => (props.state === StepState.completed ? 'auto' : 'none')};
  border-radius: 3px 3px 0 0;
  padding: 22px 15px 18px 15px;
  ${media('tablet')`
    padding: 16px 15px 16px 15px;
  `};
  line-height: 27px;
  outline: none;
  font-family: ${(props) => props.theme.font.play};
  font-size: 20px;
  font-weight: bold;
  color: ${(props) =>
    stepResolver(
      props.state,
      darken(0.01, props.theme.color.greyDark),
      props.theme.color.tomato,
      props.theme.color.grey.mid
    )};
  background-color: white;
  border-left: 5px solid
    ${(props) =>
      stepResolver(props.state, props.theme.color.primary.blue, props.theme.color.tomato, props.theme.color.grey.mid)};
  cursor: pointer;
  transition: color 0.2s;
`

const HeaderMarker = styled.span<{ state: StepState }>`
  ${(props) =>
    props.state === StepState.completed &&
    css`
      position: relative;
      text-indent: 9999px;
      &::after {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        text-indent: 0;
        font-family: 'FontAwesome';
        content: '\\f00c';
      }
    `};
  display: inline-block;
  vertical-align: top;
  margin-top: 1px;
  margin-right: 13px;
  width: 24px;
  height: 24px;
  line-height: 24px;
  font-family: ${(props) => props.theme.font.lato};
  font-size: 14px;
  font-weight: normal;
  text-align: center;
  color: white;
  background-color: ${(props) =>
    stepResolver(props.state, props.theme.color.primary.blue, props.theme.color.tomato, props.theme.color.grey.mid)};
  border-radius: 50%;
`

const HeaderLabel = styled.span`
  display: block;
  margin-top: 3px;
  float: right;
  font-family: ${(props) => props.theme.font.lato};
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.color.greyDarker};
  ${media('mobile')`
    float: none;
  `};
`

const HeaderTitle = styled.span`
  overflow: hidden;
  margin-right: 8px;
`

const Content = styled.div`
  padding: 30px;
  border-top: 1px solid ${(props) => props.theme.color.greyLight};
  ${media('tablet')`
    padding: 20px 15px;
  `};
  ${media('mobile')`
    padding: 15px 10px;
  `};
`

const Headline = styled.span`
  display: block;
  margin-bottom: 18px;
  font-family: ${(props) => props.theme.font.lato};
  font-size: 14px;
  font-weight: 900;
  line-height: 21px;
  color: ${(props) => props.theme.color.greyDarker};
  text-transform: uppercase;
`

const ContentContainer = styled.div`
  height: 0;
  overflow: hidden;
`

export enum StepState {
  inactive,
  active,
  completed
}

type Props = {
  marker: string
  title: string
  headline?: string
  label?: string
  state: StepState
  onClick?: () => void
}

class Step extends React.Component<Props> {
  render() {
    return (
      <Container state={this.props.state}>
        <Header onClick={this.props.onClick} state={this.props.state}>
          <HeaderMarker state={this.props.state}>{this.props.marker}</HeaderMarker>
          <HeaderTitle>{this.props.title}</HeaderTitle>
          {this.props.label != null && this.props.state !== StepState.inactive && (
            <HeaderLabel>({this.props.label})</HeaderLabel>
          )}
        </Header>
        <Transition
          items={this.props.state === StepState.active}
          from={{ opacity: 0, height: 0 }}
          enter={{ opacity: 1, height: 'auto' }}
          leave={[{ opacity: 0, height: 0 }]}
          config={{ ...config.default, precision: 0.01 }}
        >
          {(show) =>
            show &&
            ((props) => (
              <ContentContainer style={props}>
                <Content>
                  {this.props.headline != null && <Headline>{this.props.headline}</Headline>}
                  {this.props.children}
                </Content>
              </ContentContainer>
            ))
          }
        </Transition>
      </Container>
    )
  }
}

export default Step
